import React, { useMemo, useState } from "react";
import { ZoomIn } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Slider } from "@/components/ui/slider";
import {
  DEFAULT_CROP_ZOOM,
  MAX_CROP_ZOOM,
  MIN_CROP_ZOOM,
  getCropZoom,
  type CropCenterKeyframe,
} from "@/hooks/use-auto-save-crop-keyframes";
import { applySceneCropFraming, resolveSceneCropTarget } from "@/lib/crop/sceneCropFraming";

interface Props {
  keyframes: CropCenterKeyframe[];
  /** The clip's scenes (source time). The zoom only changes the one under the playhead. */
  scenes: ReadonlyArray<{ startTime: number; endTime: number }>;
  currentTime: number;
  onChange: (next: CropCenterKeyframe[]) => void;
  className?: string;
}

/** Quick crop zoom for the scene under the playhead (the editor toolbar's
 *  reframe button). Drags preview locally and commit one edit on release. */
export const SceneZoomControl: React.FC<Props> = ({ keyframes, scenes, currentTime, onChange, className }) => {
  const { t } = useTranslation();
  const target = useMemo(
    () => resolveSceneCropTarget(keyframes, scenes, currentTime),
    [keyframes, scenes, currentTime],
  );
  const targetKey = target ? `${target.start}:${target.end}` : null;
  const [draft, setDraft] = useState<{ key: string; zoom: number } | null>(null);
  const zoom = draft && draft.key === targetKey
    ? draft.zoom
    : target ? getCropZoom(target.keyframe) : MIN_CROP_ZOOM;

  return (
    <div
      className={className}
      title={target
        ? `${t("reframeModal.zoomSceneOnly")}: ${zoom.toFixed(2)}x`
        : t("reframeModal.zoomEmpty")}
    >
      <ZoomIn className="h-3 w-3 shrink-0 text-muted-foreground" />
      <Slider
        min={MIN_CROP_ZOOM}
        max={MAX_CROP_ZOOM}
        step={0.05}
        value={[zoom]}
        onValueChange={([value]) => { if (targetKey) setDraft({ key: targetKey, zoom: value }); }}
        onValueCommit={([value]) => {
          setDraft(null);
          if (!target || value === getCropZoom(target.keyframe)) return;
          onChange(applySceneCropFraming({
            keyframes,
            scenes,
            range: { start: target.start, end: target.end },
            framing: {
              x: target.keyframe.x,
              y: target.keyframe.y,
              zoom: value === DEFAULT_CROP_ZOOM ? undefined : value,
            },
          }));
        }}
        disabled={!target}
        className="w-24 sm:w-28"
        thumbProps={{ "aria-label": t("reframeModal.zoomSceneOnly") }}
      />
    </div>
  );
};
